import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faFlag } from '@fortawesome/pro-solid-svg-icons/faFlag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { AvatarFavoriteButton } from 'us-react-components/dist/AvatarFavoriteButton/AvatarFavoriteButton';
import { FlagProfileModal } from 'us-react-components/dist/FlagProfileModal/FlagProfileModal';
import { MessageBubble } from 'us-react-components/dist/MessageBubble/MessageBubble';
import { MessageComposer } from 'us-react-components/dist/MessageComposer/MessageComposer';
import { PrivateNoteSection } from 'us-react-components/dist/PrivateNoteSection/PrivateNoteSection';
import {
  MessageThreadService,
  Parent,
  ParentService,
  SitterFavoritesService,
  ThreadedMessage,
  ThreadedMessageService,
} from 'us-web-services';

import { Button, ButtonSize, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { ParentNoteModal, ParentPrivateNote } from './components/ParentNoteModal';

const MAX_MESSAGE_LENGTH = 3000;

const parseMessage = (msg?: string): string => msg ?? '';

const getDistanceText = (
  t: (key: string, options?: Record<string, unknown>) => string,
  distance?: string | number,
): string => {
  if (distance == null || distance === '') return '';
  const d = Number(distance);
  if (Number.isNaN(d)) return `${distance}`;
  if (d <= 1) return t('common.distanceUnder1');
  return t('common.distanceMiles', { distance: d });
};

interface ParentWithRelationship extends Omit<Parent, 'relationship'> {
  active?: boolean;
  relationship?: {
    favorite?: { id?: number };
    blocked?: boolean;
    bookingCount?: number;
    distance?: string | number;
    note?: ParentPrivateNote;
  };
}

export const MessageThread = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const { id: pathId, parentId: parentIdParam } = useParams<{
    id: string;
    parentId: string;
  }>();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const sitterId = Number(pathId);
  const parentId = Number(parentIdParam);

  const [parent, setParent] = useState<ParentWithRelationship | null>(null);
  const [messages, setMessages] = useState<ThreadedMessage[]>([]);
  const [totalSize, setTotalSize] = useState<number>(0);
  const [newMessage, setNewMessage] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [threadId, setThreadId] = useState<number | undefined>(undefined);
  const [showHiddenModal, setShowHiddenModal] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.title = 'UrbanSitter - Message Thread';
  }, []);

  useEffect(() => {
    if (!user?.id || !sitterId || !parentId) return;
    if (sitterId !== user.id) {
      window.location.href = `/app/sitter/${user.id}/messages`;
      return;
    }

    void (async (): Promise<void> => {
      try {
        const resp = await ParentService.get(parentId, { params: { userId: user.id, sitterId: user.id } });
        setParent((resp.data?.data ?? null) as ParentWithRelationship | null);
      } catch (err) {
        console.warn('[MessageThread] parent load failed', err);
      }
    })();

    const fetchInitial = async (): Promise<void> => {
      try {
        const resp = await ThreadedMessageService.getByFilter({
          params: { parentId, sitterId, userId: user.id },
        });
        const data = resp.data?.data ?? [];
        setMessages(data);
        setTotalSize(resp.data?.meta?.size ?? data.length);
        const firstThreadId = data[0]?.threadId;
        if (firstThreadId) {
          setThreadId(firstThreadId);
          // Mark as read inline here (not in a downstream effect) so it
          // runs before any async handler could interrupt — mirrors
          // message-thread.js:157 calling markAsRead() directly from
          // setThreadId, avoiding the previous useEffect race where
          // a quick unmount could skip the mark.
          if (user.id && sitterId && parentId) {
            try {
              await MessageThreadService.patch(
                firstThreadId,
                { id: firstThreadId, read: new Date().toISOString() },
                { params: { parentId, sitterId, userId: user.id } },
              );
            } catch {
              /* ignore */
            }
          }
        }
      } catch (err) {
        console.warn('[MessageThread] fetch failed', err);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchInitial();
  }, [user?.id, sitterId, parentId]);

  useEffect(() => {
    if (!isLoading && messages.length > 0 && endRef.current) {
      endRef.current.scrollIntoView({ block: 'end' });
    }
  }, [isLoading, messages.length]);

  // Preserve scroll position when older messages are prepended via fetchPrevious
  // — otherwise the user gets snapped to the top.
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const preserveScrollHeightRef = useRef<number | null>(null);
  useEffect(() => {
    const container = messagesScrollRef.current;
    const previousHeight = preserveScrollHeightRef.current;
    if (container && previousHeight != null) {
      container.scrollTop = container.scrollHeight - previousHeight;
      preserveScrollHeightRef.current = null;
    }
  }, [messages.length]);

  const fetchPrevious = async (): Promise<void> => {
    if (!messages.length || !user?.id) return;
    const lastId = messages[0].id;
    if (messagesScrollRef.current) {
      preserveScrollHeightRef.current = messagesScrollRef.current.scrollHeight;
    }
    try {
      const resp = await ThreadedMessageService.getByFilter({
        params: {
          parentId,
          sitterId,
          userId: user.id,
          q: `id.lt(${lastId})`,
        },
      });
      const older = resp.data?.data ?? [];
      setMessages((prev) => [...older, ...prev]);
    } catch (err) {
      console.warn('[MessageThread] fetchPrevious failed', err);
    }
  };

  const createMessage = async (): Promise<void> => {
    if (!newMessage.trim() || isSending || !parent?.id || !user?.id) return;
    const hidden = (user as { hiddenReason?: number }).hiddenReason;
    if (hidden !== undefined && hidden !== 0) {
      setShowHiddenModal(true);
      return;
    }
    setIsSending(true);
    try {
      const resp = await ThreadedMessageService.create({
        parent: { id: parent.id },
        sitter: { id: sitterId },
        authorId: user.id,
        message: newMessage,
        ...(threadId ? { threadId } : {}),
      } as unknown as ThreadedMessage);
      const saved = resp.data?.data;
      if (saved) {
        setMessages((prev) => [...prev, saved]);
        setTotalSize((s) => s + 1);
        if (!threadId && saved.threadId) setThreadId(saved.threadId);
      }
      setNewMessage('');
    } catch (err) {
      const errors = (err as { response?: { data?: { errors?: Array<{ code?: string }> } } })?.response?.data?.errors;
      const isPlanError = errors?.some((e) => e.code && e.code.indexOf('threadedMessage.plan') > -1);
      if (isPlanError) {
        toast.error(t('pages.messageThread.planExpired'));
      } else {
        console.warn('[MessageThread] send failed', err);
      }
    } finally {
      setIsSending(false);
    }
  };

  const toggleFavorite = async (): Promise<void> => {
    if (!parent?.id || !user?.id) return;
    try {
      const favoriteId = parent.relationship?.favorite?.id;
      if (favoriteId) {
        await SitterFavoritesService.delete(favoriteId);
        setParent((prev) =>
          prev ? { ...prev, relationship: { ...(prev.relationship ?? {}), favorite: undefined } } : prev,
        );
      } else {
        const resp = await SitterFavoritesService.create({ sitterId: user.id, parentId: parent.id });
        const created = resp.data?.data as { id?: number } | undefined;
        setParent((prev) =>
          prev ? { ...prev, relationship: { ...(prev.relationship ?? {}), favorite: created } } : prev,
        );
      }
    } catch {
      toast.error(t('pages.messageThread.favoriteUpdateError'));
    }
  };

  const onNoteSaved = (note?: ParentPrivateNote): void => {
    setParent((prev) =>
      prev ? { ...prev, relationship: { ...(prev.relationship ?? {}), note } } : prev,
    );
  };

  const isSameSender = (index: number): boolean => {
    const cur = messages[index] as ThreadedMessage & { authorId?: number };
    const prev = messages[index - 1] as ThreadedMessage & { authorId?: number };
    return index > 0 && cur?.authorId === prev?.authorId;
  };

  if (!parent) {
    return <div className='container py-8' />;
  }

  const parentActive = parent.active !== false;

  return (
    <div className='container py-6 max-w-5xl mx-auto'>
      <h1 className='text-2xl font-semibold mb-1 hidden sm:block'>
        {t('pages.messageThread.messageNameHeading', { name: parent.firstName })}
      </h1>
      {messages.length === 0 && parentActive && (
        <p className='italic mb-4 hidden sm:block'>{t('pages.messageThread.startConversation')}</p>
      )}

      <div
        id='parent-topbar'
        className='us-card rounded-[20px] shadow-sm sm:hidden flex items-start gap-3'
      >
        <a id={`parent-${parent.id}-topbar`} href={`/app/parent/${parent.id}`} className='flex-shrink-0'>
          <img
            alt={parent.firstName ?? ''}
            src={parent.pictureUrl}
            className='w-14 h-14 rounded-full object-cover'
          />
        </a>
        <div className='flex-1 min-w-0 text-sm'>
          {parentActive ? (
            <a
              id='parent-name-to-profile-topbar'
              href={`/app/parent/${parent.id}`}
              className='font-semibold block'
            >
              {parent.firstName}
            </a>
          ) : (
            <h3 className='font-semibold mb-0'>
              {t('common.noLongerActive', { name: parent.firstName })}
            </h3>
          )}
          <div className='text-neutral-600'>
            {t('common.bookingCount', {
              count: parent.relationship?.bookingCount ?? 0,
            })}
            {parent.address?.city && <><br />{parent.address.city}</>}
            {parent.relationship?.distance && (
              <span> ({getDistanceText(t, parent.relationship.distance)})</span>
            )}
          </div>
        </div>
        <div id='dropdown-topbar' className='relative flex-shrink-0'>
          <Button
            buttonType={ButtonType.secondary}
            buttonSize={ButtonSize.small}
            className='!min-w-0 !px-2 !py-1'
            aria-label={t('common.moreOptionsAriaLabel')}
            onClick={(): void => setDropdownOpen((o) => !o)}
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </Button>
          {dropdownOpen && (
            <>
              <div
                className='fixed inset-0 z-10'
                onClick={(): void => setDropdownOpen(false)}
                onKeyDown={(): void => setDropdownOpen(false)}
                role='presentation'
              />
              <ul
                className='absolute right-0 mt-1 bg-white border border-neutral-200 rounded shadow-lg z-20 min-w-max'
                role='menu'
              >
                <li role='menuitem' className='py-1'>
                  <a
                    href={`/app/bookings?filterType=all_active&parentId=${parent.id}`}
                    className='flex items-center gap-2 px-4 py-2 hover:bg-neutral-100 text-sm'
                    onClick={(): void => setDropdownOpen(false)}
                  >
                    <FontAwesomeIcon icon={faCircleCheck} className='text-sm' />
                    {t('common.viewBookings')}
                  </a>
                </li>
                <li role='menuitem' className='py-1'>
                  <Button
                    buttonType={ButtonType.tertiary}
                    className='flex items-center gap-2 px-4 py-2 hover:bg-neutral-100 text-sm w-full text-left'
                    onClick={(): void => { setDropdownOpen(false); setFlagOpen(true); }}
                  >
                    <FontAwesomeIcon icon={faFlag} className='text-sm' />
                    {t('common.reportIssue')}
                  </Button>
                </li>
              </ul>
            </>
          )}
        </div>
      </div>

      <div id='message-thread-container' className='grid grid-cols-1 sm:grid-cols-12 gap-4'>
        <div className='sm:col-span-8 md:col-span-9'>
          {messages.length > 0 && (
            <div
              ref={messagesScrollRef}
              className='us-card rounded-[20px] shadow-sm max-h-[60vh] overflow-y-auto'
            >
              {totalSize > messages.length && (
                <Button
                  buttonType={ButtonType.tertiary}
                  id='load-previous-messages'
                  onClick={fetchPrevious}
                  className='block text-center mb-3 underline cursor-pointer bg-transparent border-0 p-0 w-full'
                >
                  {t('pages.messageThread.viewPrevious')}
                </Button>
              )}
              {messages.map((msg, index) => {
                const authorId = (msg as ThreadedMessage & { authorId?: number }).authorId;
                const isSelf = authorId !== parent.id;
                const isRepeat = isSameSender(index);
                return (
                  <MessageBubble
                    key={msg.id ?? index}
                    id={`message-${index}`}
                    isSelf={isSelf}
                    isRepeat={isRepeat}
                    message={parseMessage(msg.message)}
                    label={msg.label}
                    avatarSrc={parent.pictureUrl}
                    avatarAlt={parent.firstName ?? ''}
                    avatarHref={`/app/parent/${parent.id}`}
                  />
                );
              })}
              <div ref={endRef} />
            </div>
          )}

          {parentActive && (
            <MessageComposer
              value={newMessage}
              onChange={setNewMessage}
              onSend={createMessage}
              isSending={isSending}
              maxLength={MAX_MESSAGE_LENGTH}
              recipientName={parent.firstName}
              ariaLabel={t('pages.messageThread.messageLabelTo', { name: parent.firstName })}
              placeholder={t('pages.messageThread.typeMessage', { name: parent.firstName })}
              sendAriaLabel={t('pages.messageThread.sendAriaLabel')}
            />
          )}
        </div>

        {/* Parent sidebar */}
        <aside id='parent-sidebar' className='sm:col-span-4 md:col-span-3 hidden sm:block'>
          <div id='parent-sidebar-card' className='us-card rounded-[20px] shadow-sm'>
            <div className='relative inline-block mb-3'>
              <a id={`parent-${parent.id}`} href={`/app/parent/${parent.id}`}>
                <img
                  alt={parent.firstName ?? ''}
                  src={parent.pictureUrl}
                  className='w-20 h-20 rounded-full object-cover'
                />
              </a>
              {!parent.relationship?.blocked && parentActive && (
                <AvatarFavoriteButton
                  isFavorite={!!parent.relationship?.favorite?.id}
                  onToggle={toggleFavorite}
                  ariaLabel={parent.relationship?.favorite?.id ? 'Remove from favorites' : 'Add to favorites'}
                />
              )}
            </div>
            <div id='sidebar-dropdown' className='relative inline-block float-right'>
              <Button
                buttonType={ButtonType.secondary}
                buttonSize={ButtonSize.small}
                className='!py-1'
                aria-label={t('common.moreOptionsAriaLabel')}
                onClick={(): void => setDropdownOpen((o) => !o)}
              >
                <FontAwesomeIcon icon={faChevronDown} />
              </Button>
              {dropdownOpen && (
                <>
                  <div
                    className='fixed inset-0 z-10'
                    onClick={(): void => setDropdownOpen(false)}
                    onKeyDown={(): void => setDropdownOpen(false)}
                    role='presentation'
                  />
                  <ul
                    className='absolute right-0 mt-1 bg-white border border-neutral-200 rounded shadow-lg z-20 min-w-max'
                    role='menu'
                  >
                    <li role='menuitem' className='py-1'>
                      <a
                        href={`/app/bookings?filterType=all_active&parentId=${parent.id}`}
                        className='flex items-center gap-2 px-4 py-2 hover:bg-neutral-100 text-sm'
                        onClick={(): void => setDropdownOpen(false)}
                      >
                        <FontAwesomeIcon icon={faCircleCheck} className='text-sm' />
                        {t('common.viewBookings')}
                      </a>
                    </li>
                    <li role='menuitem' className='py-1'>
                      <Button
                        buttonType={ButtonType.tertiary}
                        className='flex items-center gap-2 px-4 py-2 hover:bg-neutral-100 text-sm w-full text-left'
                        onClick={(): void => { setDropdownOpen(false); setFlagOpen(true); }}
                      >
                        <FontAwesomeIcon icon={faFlag} className='text-sm' />
                        {t('common.reportIssue')}
                      </Button>
                    </li>
                  </ul>
                </>
              )}
            </div>
            {parentActive ? (
              <a
                id='parent-name-to-profile-sidebar'
                href={`/app/parent/${parent.id}`}
                className='font-semibold block mb-1 clear-both'
              >
                {parent.firstName}
              </a>
            ) : (
              <p className='font-semibold mb-1 clear-both'>
                {t('common.noLongerActive', { name: parent.firstName })}
              </p>
            )}
            <p className='text-sm text-neutral-600 mb-2'>
              {t('common.bookingCount', {
                count: parent.relationship?.bookingCount ?? 0,
              })}
              {parent.address?.city && <><br />{parent.address.city}</>}
              {parent.relationship?.distance && (
                <span> ({getDistanceText(t, parent.relationship.distance)})</span>
              )}
            </p>
            <PrivateNoteSection
              note={parent.relationship?.note}
              onEdit={(): void => setNoteOpen(true)}
            />
          </div>
        </aside>
      </div>

      {parent.id && user?.id && (
        <ParentNoteModal
          isOpen={noteOpen}
          onClose={(): void => setNoteOpen(false)}
          sitterId={user.id}
          parentId={parent.id}
          initialNote={parent.relationship?.note}
          onSaved={onNoteSaved}
        />
      )}
      {parent.id && user?.id && (
        <FlagProfileModal
          isOpen={flagOpen}
          onClose={(): void => setFlagOpen(false)}
          flaggedId={parent.id}
          flaggingId={user.id}
        />
      )}

      {showHiddenModal && (
        <div
          className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'
          role='button'
          tabIndex={0}
          aria-label={t('pages.messageThread.closeModalAriaLabel')}
          onClick={(): void => setShowHiddenModal(false)}
          onKeyDown={(e): void => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowHiddenModal(false);
            }
          }}
        >
          <div
            className='bg-white rounded max-w-md mx-4 w-full'
            role='presentation'
            onClick={(e): void => e.stopPropagation()}
            onKeyDown={(e): void => e.stopPropagation()}
          >
            <div className='flex justify-between items-center p-4 border-b'>
              <h3 id='hidden-profile-modal-title' className='font-semibold text-lg'>
                {t('pages.messageThread.hiddenHeading')}
              </h3>
              <Button
                buttonType={ButtonType.tertiary}
                aria-label={t('pages.messageThread.closeModalAriaLabel')}
                className='text-2xl leading-none'
                onClick={(): void => setShowHiddenModal(false)}
              >
                ×
              </Button>
            </div>
            <div className='p-5'>
              <p className='mb-4'>{t('pages.messageThread.hiddenBody')}</p>
            </div>
            <div className='flex justify-end p-4 border-t'>
              <Button
                buttonType={ButtonType.primary}
                className='px-6'
                onClick={(): void => setShowHiddenModal(false)}
              >
                {t('pages.messageThread.hiddenOk')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
